import { DemoAIFillingType } from './enums';

export type TDemoAIFillingTextSubstep = {
  type: 'text';
  title: string;
  description: string;
  icon: 'lottie' | 'company_house' | string;
  durationMs: number;
};

export type TDemoAIFillingQuestionSubstep = {
  type: 'question';
  title: string;
  description: string;
  yesText: string;
  noText: string;
};

export type TDemoAIFillingSubstep =
  | TDemoAIFillingTextSubstep
  | TDemoAIFillingQuestionSubstep;

export const DEMO_AI_FILLING_SUBSTEPS: Record<
  DemoAIFillingType,
  TDemoAIFillingSubstep[]
> = {
  [DemoAIFillingType.TM01_DIRECTOR_TERMINATION]: [
    {
      type: 'text',
      title: 'Opening Companies House WebFiling portal',
      description: 'Website loaded',
      icon: 'company_house',
      durationMs: 5000,
    },
    {
      type: 'text',
      title: 'Navigating to director cessation filing',
      description: 'TM01 form page opened',
      icon: 'company_house',
      durationMs: 4000,
    },
    {
      type: 'text',
      title: 'Entering company number',
      description: 'Director matched',
      icon: 'lottie',
      durationMs: 6000,
    },
    {
      type: 'text',
      title: 'Entering cessation date',
      description: 'Date entered',
      icon: 'lottie',
      durationMs: 5000,
    },
    {
      type: 'question',
      title: 'Allow Commenda Agent to access Companies House WebFiling?',
      description:
        'The agent will open the Companies House filing portal and submit the prepared TM01 form.',
      yesText: 'Allow',
      noText: 'Cancel',
    },
    {
      type: 'text',
      title: 'Submitting the filing',
      description: 'Filing submitted to Companies House',
      icon: 'company_house',
      durationMs: 7000,
    },
    {
      type: 'text',
      title: 'Confirming submission',
      description: 'Filing accepted successfully',
      icon: 'lottie',
      durationMs: 4000,
    },
  ],
  [DemoAIFillingType.UBO_PSC_UPDATE]: [
    {
      type: 'text',
      title: 'Opening ACRA BizFile+ portal',
      description: 'Website loaded',
      icon: 'lottie',
      durationMs: 5000,
    },
    {
      type: 'text',
      title: 'Authenticating with CorpPass',
      description: 'Login successful',
      icon: 'lottie',
      durationMs: 4000,
    },
    {
      type: 'text',
      title: 'Navigating to Register of Registrable Controllers',
      description: 'RORC section opened',
      icon: 'lottie',
      durationMs: 3000,
    },
    {
      type: 'text',
      title: 'Reviewing current beneficial ownership records',
      description: 'Records retrieved',
      icon: 'lottie',
      durationMs: 5000,
    },
    {
      type: 'text',
      title: 'Filling in updated beneficial owner details',
      description: 'Details entered',
      icon: 'lottie',
      durationMs: 7000,
    },
    {
      type: 'question',
      title: 'Allow Commenda Agent to access ACRA BizFile+?',
      description:
        'The agent will submit the updated beneficial ownership details to ACRA via BizFile+.',
      yesText: 'Allow',
      noText: 'Cancel',
    },
    {
      type: 'text',
      title: 'Submitting the update to ACRA',
      description: 'Update submitted to ACRA',
      icon: 'lottie',
      durationMs: 7000,
    },
    {
      type: 'text',
      title: 'Confirming submission',
      description: 'Beneficial ownership update accepted successfully',
      icon: 'lottie',
      durationMs: 4000,
    },
  ],
  [DemoAIFillingType.VERIFY_SALES_TAX_CREDENTIALS]: [
    {
      type: 'text',
      title: 'Opening state tax portal',
      description: 'Portal loaded',
      icon: 'lottie',
      durationMs: 5000,
    },
    {
      type: 'text',
      title: 'Locating login page',
      description: 'Login page found',
      icon: 'lottie',
      durationMs: 3000,
    },
    {
      type: 'text',
      title: 'Entering stored credentials',
      description: 'Credentials entered',
      icon: 'lottie',
      durationMs: 5000,
    },
    {
      type: 'text',
      title: 'Authenticating with tax authority',
      description: 'Authentication successful',
      icon: 'lottie',
      durationMs: 5000,
    },
    {
      type: 'text',
      title: 'Verifying account access and permissions',
      description: 'Access confirmed',
      icon: 'lottie',
      durationMs: 4000,
    },
    {
      type: 'question',
      title: 'Allow Commenda Agent to verify sales tax credentials?',
      description:
        'The agent will log in to the state tax portal and verify that the stored credentials are valid.',
      yesText: 'Allow',
      noText: 'Cancel',
    },
    {
      type: 'text',
      title: 'Testing filing submission access',
      description: 'Filing permissions verified',
      icon: 'lottie',
      durationMs: 6000,
    },
    {
      type: 'text',
      title: 'Confirming credential verification',
      description: 'Sales tax credentials verified successfully',
      icon: 'lottie',
      durationMs: 3000,
    },
  ],
};

export type TDemoAIFillingGroupedStep = {
  title: string;
  substeps: string[];
  titleDelayMs?: number;
  substepDelayMs?: number;
};

export const DEMO_AI_FILLING_GROUPED_STEPS: Record<
  DemoAIFillingType,
  TDemoAIFillingGroupedStep[]
> = {
  [DemoAIFillingType.TM01_DIRECTOR_TERMINATION]: [
    {
      title: 'Visiting companieshouse.gov.uk',
      titleDelayMs: 0,
      substepDelayMs: 2000,
      substeps: [
        'Opened secure browser session',
        'Navigated to Companies House WebFiling service',
        'Authenticated with filing credentials',
      ],
    },
    {
      title: 'Working on find-and-update.company-information.service.gov.uk',
      titleDelayMs: 3000,
      substepDelayMs: 2500,
      substeps: [
        'Searched for company by registration number',
        'Confirmed registered office address and active status',
        'Selected "File a TM01 — Termination of appointment as director"',
      ],
    },
    {
      title: 'Working on ewf.companieshouse.gov.uk',
      titleDelayMs: 3500,
      substepDelayMs: 3000,
      substeps: [
        'Selected director from officer list',
        'Entered cessation date',
        'Confirmed reason for termination',
      ],
    },
    {
      title: 'Working on ewf.companieshouse.gov.uk',
      titleDelayMs: 3000,
      substepDelayMs: 2500,
      substeps: [
        'Reviewed completed TM01 form details',
        'Verified all required fields are populated',
        'Submitted filing for approval',
      ],
    },
  ],
  [DemoAIFillingType.UBO_PSC_UPDATE]: [
    {
      title: 'Visiting bizfile.gov.sg',
      titleDelayMs: 0,
      substepDelayMs: 2000,
      substeps: [
        'Opened secure browser session',
        'Navigated to ACRA BizFile+ portal',
        'Authenticated with CorpPass credentials',
      ],
    },
    {
      title: 'Working on bizfile.gov.sg',
      titleDelayMs: 3000,
      substepDelayMs: 2500,
      substeps: [
        'Retrieved current Register of Registrable Controllers',
        'Identified beneficial ownership records requiring update',
        'Selected "Update Register of Registrable Controllers"',
      ],
    },
    {
      title: 'Working on bizfile.gov.sg',
      titleDelayMs: 3500,
      substepDelayMs: 3000,
      substeps: [
        'Updated beneficial owner cessation details',
        'Entered date of change',
        'Confirmed updated ownership structure',
      ],
    },
    {
      title: 'Working on bizfile.gov.sg',
      titleDelayMs: 3000,
      substepDelayMs: 2500,
      substeps: [
        'Reviewed completed RORC update details',
        'Verified all required fields are populated',
        'Submitted update to ACRA for approval',
      ],
    },
  ],
  [DemoAIFillingType.VERIFY_SALES_TAX_CREDENTIALS]: [
    {
      title: 'Visiting state tax portal',
      titleDelayMs: 0,
      substepDelayMs: 2000,
      substeps: [
        'Opened secure browser session',
        'Navigated to state tax authority website',
        'Located login page',
      ],
    },
    {
      title: 'Working on tax portal authentication',
      titleDelayMs: 3000,
      substepDelayMs: 3000,
      substeps: [
        'Entered stored credentials',
        'Completed authentication challenge',
        'Verified account access and permissions',
      ],
    },
    {
      title: 'Working on credential verification',
      titleDelayMs: 3500,
      substepDelayMs: 2500,
      substeps: [
        'Tested filing submission access',
        'Confirmed tax registration status',
        'Verified sales tax credentials successfully',
      ],
    },
  ],
};

export const DEMO_AI_FILLING_TOTAL_DURATION_MS: Record<
  DemoAIFillingType,
  number
> = Object.fromEntries(
  Object.entries(DEMO_AI_FILLING_SUBSTEPS).map(([key, steps]) => [
    key,
    steps.reduce(
      (sum, step) => sum + (step.type === 'text' ? step.durationMs : 0),
      0
    ),
  ])
) as Record<DemoAIFillingType, number>;

/**
 * The index of the last grouped step to show BEFORE the permission question.
 * Groups 0..questionAfterGroupIndex are shown pre-question,
 * the remaining groups are shown post-question.
 */
export const DEMO_AI_FILLING_QUESTION_AFTER_GROUP: Record<
  DemoAIFillingType,
  number
> = {
  [DemoAIFillingType.TM01_DIRECTOR_TERMINATION]: 2,
  [DemoAIFillingType.UBO_PSC_UPDATE]: 2,
  [DemoAIFillingType.VERIFY_SALES_TAX_CREDENTIALS]: 1,
};
